import queryString from 'query-string'

/**
 * The Directory page persists its filter / pagination / view state in the URL
 * query string. This module is the single source of truth for the encoding —
 * Index.js reads the URL through `parseDirectoryUrl`, the DesktopView writes
 * to it through `pushDirectoryUrl` / `replaceDirectoryUrl`. Reducer state
 * mirrors the URL; the URL is authoritative.
 *
 * Encoding:
 *   ?category=10           selected category id (omitted when null)
 *   ?tags=1,3,5            comma-separated tag ids (omitted when [])
 *   ?q=manual              search text (omitted when "")
 *   ?page=2                1-based pagination (omitted when 1)
 *   ?view=grid             list (default) / grid (only emitted when grid)
 *
 * Defaults are omitted so the URL is clean when nothing is set.
 */

const DEFAULT_STATE = Object.freeze({
	categoryId: null,
	tagIds: [],
	query: '',
	pageNumber: 1,
	gridMode: false
})

/**
 * Parse a location.search string into the typed state object. Robust against
 * malformed input: non-integer values coerce to defaults (not NaN).
 */
export const parseDirectoryUrl = (search) => {
	const parsed = queryString.parse(search || '')

	const categoryIdRaw = parsed.category
	const categoryId = toPositiveIntOrNull(categoryIdRaw)

	const tagIds = parseTagsCsv(parsed.tags)

	const query = typeof parsed.q === 'string' ? parsed.q : ''

	const pageNumberRaw = parsed.page
	const pageNumber = toPositiveIntOrNull(pageNumberRaw) || DEFAULT_STATE.pageNumber

	const gridMode = parsed.view === 'grid'

	return { categoryId, tagIds, query, pageNumber, gridMode }
}

/**
 * Build a query string (with leading `?` when non-empty, empty when all
 * fields are at default). Useful for building URLs from full state objects.
 */
export const buildDirectoryUrl = (state) => {
	const merged = { ...DEFAULT_STATE, ...(state || {}) }
	const obj = {}

	if (merged.categoryId != null) obj.category = String(merged.categoryId)
	if (Array.isArray(merged.tagIds) && merged.tagIds.length > 0) obj.tags = merged.tagIds.join(',')
	if (merged.query) obj.q = merged.query
	if (merged.pageNumber && merged.pageNumber > 1) obj.page = String(merged.pageNumber)
	if (merged.gridMode) obj.view = 'grid'

	const qs = queryString.stringify(obj)
	return qs ? `?${qs}` : ''
}

/**
 * Merge a partial state with the URL's current state and push the new URL
 * onto the history stack. Use for "navigational" actions — clicking a
 * category, toggling a tag, changing the page, switching view mode — so
 * the back button restores the previous combo.
 */
export const pushDirectoryUrl = (history, partialState) => {
	const current = parseDirectoryUrl(history.location.search)
	const next = { ...current, ...partialState }
	const search = buildDirectoryUrl(next)
	history.push({ pathname: history.location.pathname, search })
}

/**
 * Same as `pushDirectoryUrl` but replaces the current history entry instead
 * of adding a new one. Use for ephemeral input that shouldn't pollute the
 * back stack — primarily the debounced search box, where each keystroke
 * would otherwise add a `q=m`, `q=ma`, ... entry.
 */
export const replaceDirectoryUrl = (history, partialState) => {
	const current = parseDirectoryUrl(history.location.search)
	const next = { ...current, ...partialState }
	const search = buildDirectoryUrl(next)
	history.replace({ pathname: history.location.pathname, search })
}

// ── helpers ─────────────────────────────────────────────────────────────────

const toPositiveIntOrNull = (raw) => {
	if (raw == null || raw === '') return null
	const n = parseInt(raw, 10)
	return Number.isInteger(n) && n > 0 ? n : null
}

const parseTagsCsv = (raw) => {
	if (!raw) return []
	// queryString.parse returns a string for `?tags=1,2,3` (slice-5 helper
	// format) and an array for `?tags=1&tags=2&tags=3` (the format
	// FilterSelect writes when users toggle chips in the services-dashboard
	// filter component we adopted in slice 8). Accept both so the round-trip
	// works regardless of which writer last touched the URL.
	const parts = Array.isArray(raw) ? raw : String(raw).split(',')
	return parts
		.map((p) => toPositiveIntOrNull(typeof p === 'string' ? p.trim() : p))
		.filter((id) => id != null)
}

export const DIRECTORY_DEFAULTS = DEFAULT_STATE
